import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import {

  createUserWithIdp,
  onCheckPhoneNumberExists,
  onCreateCustomer,
  onShowUser,
  sendMailToUser,
  sendVerificationOtp,
} from '../util/api';
import { storableError } from '../util/errors';
import * as log from '../util/log';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import mixpanel from 'mixpanel-browser';
import { mixPanelEventsConstants, mixPanelEventsData, mixPanelEventsScreens } from '../mixpanel';
import { initialCurrentUser, trackEventsMixpanel } from '../mixpanel/mixpanel';
const authenticated = authInfo => authInfo?.isAnonymous === false;
const loggedInAs = authInfo => authInfo?.isLoggedInAs === true;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // is marketplace operator logged in as a marketplace user
  isLoggedInAs: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        isLoggedInAs: loggedInAs(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logoutInProgress: false,
        isAuthenticated: false,
        isLoggedInAs: false,
        authScopes: [],
      };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

//  const body = {
//    firstName: currentUser?.attributes?.profile.firstName, // Customer's first name
//    lastName: currentUser?.attributes?.profile.lastName, // Customer's last name
//    email: currentUser?.attributes?.email, // Customer's email address
//    phone: {
//      country_code: '20', // Country code for the phone number
//      number: '10000000', // The phone number
//    },
//  };

//  const data = await onCreateCustomer(body);

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

// const body = {
//   firstName: currentUser?.attributes?.profile.firstName, // Customer's first name
//   lastName: currentUser?.attributes?.profile.lastName, // Customer's last name
//   email: currentUser?.attributes?.email, // Customer's email address
//   phone: {
//     country_code: '20', // Country code for the phone number
//     number: '10000000', // The phone number
//   },
// };

// const data = await onCreateCustomer(body);

export const login = (username, password) => async (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  dispatch(loginRequest());

  try {
    // --- Step 1: Check if user already exists by email ---
    try {
      const fetchedUser = await onShowUser({ email: username });
      const { isDeleted } = fetchedUser?.data?.data?.data?.attributes?.profile?.metadata || {};

      if (isDeleted) {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
        throw new Error('deleted-user');
      }
    } catch (checkUserError) {
      if (checkUserError.message === 'deleted-user') {
        dispatch(loginError(storableError(checkUserError)));
        return; // ⛔️ Stop here if user is deleted
      }

      console.warn('User check failed (non-blocking):', checkUserError);
      // Continue login if the issue wasn't about deletion
    }

    // --- Step 2: Proceed with login ---
    await sdk.login({ username, password });
    dispatch(loginSuccess());

    const user = await dispatch(fetchCurrentUser());
    const { publicData } = user?.attributes?.profile || {};
    const { userType } = publicData || {};
    const userId = user.id.uuid;

    trackEventsMixpanel(mixPanelEventsConstants.USER_SIGNED_IN, {
      screen: mixPanelEventsScreens.SIGNUP,
      language: 'en',
      userType: userType === 'customer' ? 'Customer' : 'Provider',
      userId: userId,
      action_type: 'user_signed_in',
    });

  } catch (e) {
    dispatch(loginError(storableError(e)));
  }
};


export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => async (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  dispatch(signupRequest());

  const { phoneNumber } = params?.protectedData || {};

  try {
    // --- Step 1: Check if user already exists by email ---
    try {
      const fetchedUser = await onShowUser({ email: params?.email });
      const { isDeleted } = fetchedUser?.data?.data?.data?.attributes?.profile?.metadata || {};

      if (isDeleted) {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
        throw new Error('deleted-user'); // ❌ Block signup if user is deleted
      }
    } catch (checkUserError) {
      if (checkUserError.message === 'deleted-user') {
        dispatch(signupError(storableError(checkUserError)));
        return; // ⛔️ Stop here
      }

      console.warn('User check failed (non-blocking):', checkUserError);
      // Continue if the issue wasn't about deletion
    }

    // --- Step 2: Check if phone number is already taken ---
    if (phoneNumber) {
      const res = await onCheckPhoneNumberExists({ phoneNumber });
      if (res?.phoneNumberExists) {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
        throw new Error('phone-taken');
      }
    }

    // --- Step 3: Create user ---
    const response = await sdk.currentUser.create(params, { expand: true });
    const user = response?.data?.data;
    const { publicData, protectedData, firstName, lastName, email } = user?.attributes?.profile || {};
    const { userType } = publicData || {};
    const createdPhoneNumber = protectedData?.phoneNumber;

    console.log('created user', response);
    console.log('createdPhoneNumber', createdPhoneNumber);

    // --- Step 4: Send OTP if phone number provided ---
    if (createdPhoneNumber && process.env.REACT_APP_ENV !== "development") {
      sendVerificationOtp({ phoneNumber: createdPhoneNumber }).catch(err => {
        console.error('OTP send failed:', err);
      });
    }

    const userId = user?.id?.uuid;

    // --- Step 5: Analytics ---
    if (userId) {
      initialCurrentUser({
        firstName: params?.firstName,
        lastName: params?.lastName,
        email: params?.email,
        userId,
        userType: params?.publicData?.userType === 'customer' ? 'Customer' : 'Provider',
      });

      trackEventsMixpanel(mixPanelEventsConstants?.USER_SIGNED_UP, {
        signup_method: 'email',
        language: 'en',
        action_type: mixPanelEventsData?.SIGN_UP,
        screen: mixPanelEventsScreens?.SIGNUP,
        user_type: params?.publicData?.userType === 'customer' ? 'Customer' : 'Provider',
        user_id: userId,
      });
    }

    dispatch(signupSuccess());

    // --- Step 6: Log user in ---
    dispatch(login(params.email, params.password));

    // sendMailToUser({
    //   from: process.env.REACT_APP_SENDGRID_ADMIN_MAIL,
    //   to: "himanshu@icodelabs.co",
    //   subject: "Complete your profile",
    //   templateId: "d-f7690419fe4c486cafce09a1de376d32",
    //   dynamicTemplateData: {
    //     "merchant_name": "Himanshu", 
    //     "Amount": 200, 
    //     "Account ID": '23232'
    //   }
    // })
    //   .then(response => {
    //     console.log("Email sent successfully:", response);
    //   })
    //   .catch(error => {
    //     console.error("Error sending email:", error);
    //   });

    return user;

  } catch (e) {
    dispatch(signupError(storableError(e)));
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
    log.error(e, 'signup-failed', {
      email: params.email,
      firstName: params.firstName,
      lastName: params.lastName,
    });
  }
};


export const signupWithIdp = params => async (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  console.log("IDP Params", params);

  const { temp, ...rest } = params || {};
  const { phoneNumber } = params?.protectedData || {};

  try {
    // --- Step 1: Check if user already exists by email ---
    try {
      const fetchedUser = await onShowUser({ email: temp?.email });
      console.log("onShowUser", fetchedUser);
      const { isDeleted } = fetchedUser?.data?.data?.data?.attributes?.profile?.metadata || {};

      if (isDeleted) {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
        throw new Error('deleted-user'); // ⛔️ Block signup if deleted
      }
    } catch (checkUserError) {
      if (checkUserError.message === 'deleted-user') {
        dispatch(confirmError(storableError(checkUserError)));
        return; // 🔒 Block signup
      }

      console.warn('User check failed (non-blocking):', checkUserError);
      // Continue if the issue wasn't deletion
    }

    // --- Step 2: Check if phone number already exists ---
    if (phoneNumber) {
      const res = await onCheckPhoneNumberExists({ phoneNumber });
      const isPhoneNumberExist = res?.phoneNumberExists;

      if (isPhoneNumberExist) {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
        throw new Error('phone-taken');
      }
    }

    // --- Step 3: Create user with IDP (e.g., Google) ---
    const response = await createUserWithIdp(rest);
    dispatch(confirmSuccess());

    const user = await dispatch(fetchCurrentUser());
    console.log("fetched user", user);

    const userId = user?.id?.uuid;
    const { publicData, protectedData, firstName, lastName, email } =
      user?.attributes?.profile || {};
    const { userType } = publicData || {};
    const createdPhoneNumber = protectedData?.phoneNumber;

    // --- Step 4: Send OTP if phone number exists ---
    if (createdPhoneNumber && process.env.REACT_APP_ENV !== "development") {
      sendVerificationOtp({ phoneNumber: createdPhoneNumber }).catch(err => {
        console.error('OTP send failed:', err);
      });
    }

    // --- Step 5: Analytics & Tracking ---
    if (userId) {
      initialCurrentUser({
        firstName,
        lastName,
        email,
        userId,
        userType: userType === 'customer' ? 'Customer' : 'Provider',
      });

      trackEventsMixpanel(mixPanelEventsConstants?.USER_SIGNED_UP, {
        signup_method: 'google',
        language: 'en',
        action_type: mixPanelEventsData?.SIGN_UP,
        screen: mixPanelEventsScreens?.SIGNUP,
        user_type: userType === 'customer' ? 'Customer' : 'Provider',
        user_id: userId,
      });
    }
    return;
  } catch (e) {
    log.error(e, 'create-user-with-idp-failed', { params });
    dispatch(confirmError(storableError(e)));
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  }
};

